<script lang="ts">
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import { MutationButton } from '@athena-lib/MutationButton';
  import { BuyStickerCard } from '@athena-lib/BuyStickerCard';
  import HeadingLarge from '@athena-lib/Text/HeadingLarge/HeadingLarge.svelte';
  import { Stack } from '@athena-lib/Stack';
  import HeadingMedium from '@athena-lib/Text/HeadingMedium/HeadingMedium.svelte';
  import Button from '@athena-lib/Button/Button/Button.svelte';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import ArrowForwardIosRounded from '@athena-lib/Icons/ArrowForwardIosRounded.svelte';
  import StackItem from '@athena-lib/Stack/StackItem.svelte';
  import ArrowBack from '@athena-lib/Icons/ArrowBack.svelte';
  import StickerShopLayout from './shared/StickerShopLayout.svelte';
  import type { FigureSourceData } from '$lib/server/external/types';
  import { getBuyStickerCardProps } from '$lib/utilities/getBuyStickerCardProps';
  import { getShoppingCart } from '$lib/shopping-cart.svelte';
  import { browser } from '$app/environment';
  import { getText } from '$lib/http';
  import { trackFigureAddToCart, trackFigureItemView } from '$lib/gtm/ecommerce-tracking';
  import type { TrackFigure } from '$lib/gtm/types';
  import { getCheckoutSdk } from '$lib/checkout.svelte';

  const { data }: { data: FigureSourceData } = $props();

  const figure = $derived(data.figure);
  const localizedData = $derived(data.localizedData);
  const categoryUrl = $derived(data.categoryUrl);
  const categoryName = $derived(data.categoryName);
  const moreFromTag = $derived(data.moreFromTag);
  const locale = $derived(data.locale);
  const designerSlug = $derived(data.figure.designer_slug);
  const designersBaseUrl = $derived(data.stickerShopMeta.stickerShopUrls?.designers);
  const designerUrl = $derived(
    designersBaseUrl && designerSlug ? `${designersBaseUrl}/${designerSlug}` : undefined,
  );
  const commonTranslations = getCommonTranslations();

  const refreshCart = getShoppingCart().refreshCart;
  const stickerShopMeta = $derived(data.stickerShopMeta);
  const checkoutSdk = $derived(getCheckoutSdk());

  const addStickerToCart = async (figureId: number) => {
    if (!browser) {
      return Promise.resolve(null);
    }
    try {
      await (checkoutSdk
        ? checkoutSdk.actions.addStickerShopLineItem(figureId, 1).then((x) => {
            if (!x.success) {
              throw new Error('failed adding sticker item');
            }
          })
        : getText(`/editor/add_sticker_library_item_to_cart/${figureId}`, {
            redirect: 'manual',
          }));
      refreshCart();
    } catch (error) {
      console.error('Error adding sticker to cart', error);
    }
  };

  const trackingData: TrackFigure = {
    figure: data.figure,
    stickerShopMeta: data.stickerShopMeta,
    localization: data.locale,
  };

  const localizationContext = getLocalizationContext();

  trackFigureItemView({
    figure: data.figure,
    stickerShopMeta: data.stickerShopMeta,
    localization: localizationContext,
  });
</script>

<StickerShopLayout hideCategories>
  <Stack space="large">
    {#if browser && window.history.length > 1}
      <StackItem>
        <Button size="small" class="flex items-center gap-2" onClick={() => window.history.back()}>
          <ArrowBack sizeClass="h-5 w-5" />
          Back
        </Button>
      </StackItem>
    {/if}
    <Grid columns={{ lg: 2, md: 2, sm: 1 }}>
      <GridItem>
        <img src={figure.image_url} alt={localizedData.alt_text} class="w-128" />
      </GridItem>
      <GridItem>
        <Stack>
          <HeadingLarge class="mb-8 mt-6">{localizedData.title}</HeadingLarge>
          <p class="mb-4 text-xl">{localizedData.text}</p>
          <p class="mb-4 text-xl">{localizedData.default_sticker_info}</p>

          <p class="mb-4">
            <a class="text-st-blue-500" href={designerUrl}>{figure.created_by}</a>
          </p>
          <MutationButton
            wide
            full
            element="button"
            class="buy-${figure.id}"
            createPromise={() =>
              addStickerToCart(figure.id).finally(() => {
                if (getCheckoutSdk()) {
                  return;
                }
                trackFigureAddToCart(trackingData);
              })}
          >
            {commonTranslations.addToCart} - {localizedData.price?.unitPriceFormatted}
          </MutationButton>
        </Stack>
      </GridItem>
    </Grid>

    {#if moreFromTag?.length > 0}
      <div class="flex flex-row items-center gap-4">
        <HeadingMedium>{commonTranslations.moreStickersFromCategory} ({categoryName})</HeadingMedium
        >

        <Button
          element="a"
          class="flex flex-row items-center gap-2"
          color="dark"
          link={{ href: categoryUrl }}
          size="small"
          >{commonTranslations.showAll}
          <ArrowForwardIosRounded sizeClass="h-4 w-4"></ArrowForwardIosRounded></Button
        >
      </div>

      <Grid columns={{ md: 3, lg: 4 }}>
        {#each moreFromTag as figure}
          <GridItem>
            <BuyStickerCard
              {...getBuyStickerCardProps(figure, stickerShopMeta, locale, refreshCart)}
            />
          </GridItem>
        {/each}
      </Grid>
    {/if}
  </Stack>
</StickerShopLayout>
